import { useState, useEffect } from 'react';
import { Check, Loader2, AlertCircle, ChevronDown } from 'lucide-react';
import { GradientButton } from '@/components/common/ui/Button';
import {
  CurrencyCode,
  currencies,
  convertCurrency,
  formatPriceWithCurrency
} from '@/utils/currencyConverter';

interface ProductPlan {
  id: number;
  name: string;
  description?: string;
  periods?: Array<{
    period: number;
    price: {
      register?: number;
      renew?: number;
      wholesale?: number;
    };
  }>;
  features?: Array<{
    name: string;
    value?: string;
  }>;
}

interface ProductPlansDisplayProps {
  plans: ProductPlan[];
  loading: boolean;
  error: string | null;
  currency?: CurrencyCode;
  onCurrencyChange?: (currency: CurrencyCode) => void;
  onSelectPlan?: (plan: ProductPlan, period: number, price: number) => void;
  buttonText?: string;
  priceLabel?: string;
}

export default function ProductPlansDisplay({
  plans,
  loading,
  error,
  currency = 'USD',
  onCurrencyChange,
  onSelectPlan,
  buttonText = 'Add to Cart',
  priceLabel: _priceLabel = 'per month', // Unused but kept for interface compatibility
}: ProductPlansDisplayProps) {
  const [isMonthly, setIsMonthly] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<{ planId: number; period: number } | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>(currency);

  // Sync currency when prop changes
  useEffect(() => {
    setSelectedCurrency(currency);
  }, [currency]);

  // Handle currency change
  const handleCurrencyChange = (newCurrency: CurrencyCode) => {
    setSelectedCurrency(newCurrency);
    if (onCurrencyChange) {
      onCurrencyChange(newCurrency);
    }
  };

  // Get price for selected period - show actual price for the period, not converted
  const getPrice = (plan: ProductPlan): number => {
    if (!plan.periods || plan.periods.length === 0) {
      console.warn(`Plan ${plan.id} (${plan.name}) has no periods`);
      return 0;
    }

    // Helper function to extract price from a period
    const extractPrice = (period: any): number => {
      if (!period || !period.price) return 0;

      // Try multiple price fields in order of preference
      const priceObj = period.price;
      let price = 0;

      // Try register price first (new customer price)
      if (priceObj.register !== null && priceObj.register !== undefined && !isNaN(priceObj.register) && priceObj.register > 0) {
        price = Number(priceObj.register);
      }
      // Fallback to renew price
      else if (priceObj.renew !== null && priceObj.renew !== undefined && !isNaN(priceObj.renew) && priceObj.renew > 0) {
        price = Number(priceObj.renew);
      }
      // Fallback to wholesale price
      else if (priceObj.wholesale !== null && priceObj.wholesale !== undefined && !isNaN(priceObj.wholesale) && priceObj.wholesale > 0) {
        price = Number(priceObj.wholesale);
      }
      // Try direct price field
      else if (typeof priceObj === 'number' && priceObj > 0) {
        price = priceObj;
      }

      return price;
    };

    let selectedPeriod;
    if (isMonthly) {
      // For monthly, find period 1 (1 month), or the smallest period available with a valid price
      selectedPeriod = plan.periods.find(p => p.period === 1 && extractPrice(p) > 0);
      if (!selectedPeriod) {
        // Find any period with valid price, prefer smallest
        const periodsWithPrice = plan.periods.filter(p => extractPrice(p) > 0);
        if (periodsWithPrice.length > 0) {
          selectedPeriod = periodsWithPrice.reduce((min, p) =>
            (!min || p.period < min.period) ? p : min
          );
        } else {
          // Last resort: find smallest period even if price is 0
          selectedPeriod = plan.periods.reduce((min, p) =>
            (!min || p.period < min.period) ? p : min
          );
        }
      }
    } else {
      // For yearly, find period 12 (12 months), or the closest to 12 with valid price
      selectedPeriod = plan.periods.find(p => p.period === 12 && extractPrice(p) > 0);
      if (!selectedPeriod) {
        // Find closest to 12 with valid price
        const periodsWithPrice = plan.periods.filter(p => extractPrice(p) > 0);
        if (periodsWithPrice.length > 0) {
          selectedPeriod = periodsWithPrice.reduce((closest, p) => {
            if (!closest) return p;
            const closestDiff = Math.abs(closest.period - 12);
            const currentDiff = Math.abs(p.period - 12);
            return currentDiff < closestDiff ? p : closest;
          });
        } else {
          // Last resort: find closest to 12 even if price is 0
          selectedPeriod = plan.periods.reduce((closest, p) => {
            if (!closest) return p;
            const closestDiff = Math.abs(closest.period - 12);
            const currentDiff = Math.abs(p.period - 12);
            return currentDiff < closestDiff ? p : closest;
          });
        }
      }
    }

    if (!selectedPeriod) {
      console.warn(`Plan ${plan.id} (${plan.name}) has no valid period`);
      return 0;
    }

    let price = extractPrice(selectedPeriod);

    // If price is still 0, try to find any period with a valid price
    if (price === 0) {
      for (const period of plan.periods) {
        const testPrice = extractPrice(period);
        if (testPrice > 0) {
          price = testPrice;
          selectedPeriod = period;
          break;
        }
      }
    }

    // If still 0, log warning
    if (price === 0) {
      console.warn(`Plan ${plan.id} (${plan.name}) has no valid price. Periods:`, plan.periods);
      return 0;
    }

    // Return the price as-is for the selected period
    // The price from API is already for the full period (e.g., $4.44 for 1 month, or $53.28 for 12 months)
    // For monthly view, if we have a 12-month period, show monthly equivalent
    // For yearly view, if we have a 1-month period, show yearly equivalent
    if (isMonthly && selectedPeriod.period > 1) {
      // Show monthly equivalent: divide total price by number of months
      return price / selectedPeriod.period;
    } else if (!isMonthly && selectedPeriod.period < 12) {
      // Show yearly equivalent: multiply monthly price by 12
      return price * (12 / selectedPeriod.period);
    }

    return price;
  };

  // Get the actual period number for the selected plan
  const getSelectedPeriod = (plan: ProductPlan): number => {
    if (!plan.periods || plan.periods.length === 0) return isMonthly ? 1 : 12;

    if (isMonthly) {
      const period = plan.periods.find(p => p.period === 1);
      return period ? period.period : (plan.periods[0]?.period || 1);
    } else {
      const period = plan.periods.find(p => p.period === 12);
      return period ? period.period : 12;
    }
  };

  const handleSelectPlan = (plan: ProductPlan) => {
    const priceInUSD = getPrice(plan);
    // Ensure price is a valid number and greater than 0
    let validPrice = typeof priceInUSD === 'number' && !isNaN(priceInUSD) && priceInUSD > 0 ? priceInUSD : 0;

    // Fallback: try to extract price directly if getPrice returned 0
    if (validPrice === 0 && plan.periods && plan.periods.length > 0) {
      for (const period of plan.periods) {
        const priceObj = period.price || {};
        const testPrice = priceObj.register ?? priceObj.renew ?? priceObj.wholesale;
        if (testPrice && !isNaN(testPrice) && testPrice > 0) {
          validPrice = Number(testPrice);
          if (period.period > 1 && isMonthly) {
            validPrice = validPrice / period.period;
          }
          break;
        }
      }
    }

    if (validPrice === 0) {
      console.error(`Cannot add plan ${plan.id} (${plan.name}) to cart - price is 0. Periods:`, plan.periods);
      return;
    }

    const period = getSelectedPeriod(plan);
    // Convert price to selected currency for the callback
    const convertedPrice = convertCurrency(validPrice, selectedCurrency);

    if (onSelectPlan) {
      onSelectPlan(plan, period, convertedPrice);
    }

    setSelectedPlan({ planId: plan.id, period });
  };

  if (loading) {
    return (
      <div className="container py-12 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-textColor">Loading plans...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-12">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 flex items-center gap-3 text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <div className="container py-12 text-center">
        <p className="text-textColor">No plans available at this time.</p>
      </div>
    );
  }

  return (
    <div className="container py-12">
      {/* Header */}
      <h3 className="text-3xl md:text-4xl text-center font-bold text-white md:leading-tight mb-12">
        Choose the perfect plan for your needs
      </h3>

      {/* Toggle Switch and Currency Selector */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
        <div className="inline-flex bg-white/95 rounded-full p-1 shadow-lg">
          <button
            onClick={() => setIsMonthly(true)}
            className={`px-10 py-3 rounded-full font-semibold text-base transition-all duration-300 relative ${isMonthly
              ? 'bg-blue-500 text-white shadow-md'
              : 'text-gray-700 hover:text-gray-900'
              }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setIsMonthly(false)}
            className={`px-10 py-3 rounded-full font-semibold text-base transition-all duration-300 relative ${!isMonthly
              ? 'bg-blue-500 text-white shadow-md'
              : 'text-gray-700 hover:text-gray-900'
              }`}
          >
            Yearly
          </button>
        </div>

        {/* Currency Dropdown */}
        <div className="relative">
          <select
            value={selectedCurrency}
            onChange={(e) => handleCurrencyChange(e.target.value as CurrencyCode)}
            className="appearance-none bg-[#1a1a3e] border border-white/20 rounded-lg px-4 py-3 pr-10 text-white font-semibold cursor-pointer hover:border-purple-500/50 transition-all focus:outline-none focus:ring-2 focus:ring-purple-500 min-w-[180px]"
          >
            {currencies.map((currency) => (
              <option key={currency.code} value={currency.code} className="bg-[#1a1a3e]">
                {currency.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white pointer-events-none" />
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        {plans.map((plan, index) => {
          const price = getPrice(plan);
          // Ensure price is a valid number and greater than 0
          let validPrice = typeof price === 'number' && !isNaN(price) && price > 0 ? price : 0;

          // If price is still 0, try to extract from periods directly as fallback
          if (validPrice === 0 && plan.periods && plan.periods.length > 0) {
            for (const period of plan.periods) {
              const priceObj = period.price || {};
              const testPrice = priceObj.register ?? priceObj.renew ?? priceObj.wholesale;
              if (testPrice && !isNaN(testPrice) && testPrice > 0) {
                validPrice = Number(testPrice);
                if (period.period > 1 && isMonthly) {
                  validPrice = validPrice / period.period;
                }
                break;
              }
            }
          }

          const isSelected = selectedPlan?.planId === plan.id;

          // Log warning if price is still 0
          if (validPrice === 0 && import.meta.env.DEV) {
            console.warn(`Plan ${plan.id} (${plan.name}) has price 0. Periods:`, plan.periods);
          }

          return (
            <div
              key={plan.id || index}
              className={`relative bg-cardBg backdrop-blur-xl rounded-2xl px-7 py-8 border transition-all duration-300 flex flex-col shadow-xl hover:shadow-2xl ${isSelected
                ? 'border-primary/50 shadow-primary/20 scale-105'
                : 'border-white/5 hover:border-primary/30 hover:scale-[1.02]'
                }`}
            >
              <div
                className="absolute inset-0 rounded-2xl pointer-events-none"
                style={{
                  background:
                    "radial-gradient(70% 90% at 30% 20%, rgba(139,92,246,0.25), transparent 60%), radial-gradient(70% 90% at 70% 80%, rgba(79,70,229,0.2), transparent 60%)",
                  filter: "blur(14px)",
                  opacity: 0.18,
                }}
              />
              {/* Title */}
              <div className="mb-4">
                <h2 className="text-white text-2xl font-bold leading-snug">
                  {plan.name || `Plan ${index + 1}`}
                </h2>
                {plan.description && (
                  <p className="text-gray-300 text-sm leading-relaxed mt-2">
                    {plan.description}
                  </p>
                )}
              </div>

              {/* Price */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <div className="flex items-baseline gap-1">
                  <h3 className="text-white text-4xl font-bold">
                    {formatPriceWithCurrency(
                      convertCurrency(validPrice, selectedCurrency),
                      selectedCurrency,
                      selectedCurrency === 'JPY' ? 0 : 2
                    )}
                  </h3>
                  <span className="text-gray-300 text-sm ml-1">
                    /{isMonthly ? 'month' : 'year'}
                  </span>
                </div>
              </div>

              {/* Add to Cart Button */}
              <div className="w-full mb-6">
                <GradientButton
                  onClick={() => handleSelectPlan(plan)}
                  className="w-full"
                >
                  {buttonText}
                </GradientButton>
              </div>

              {/* Features Section - Always show */}
              <div className="flex-grow border-t border-white/10 pt-6">
                <h3 className="text-white font-bold mb-4 text-lg">Features:</h3>
                {plan.features && plan.features.length > 0 ? (
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary flex items-center justify-center mt-0.5">
                          <Check className="w-3 h-3 text-white" strokeWidth={3} />
                        </div>
                        <span className="text-gray-300 text-sm leading-relaxed">
                          {feature.name}
                          {feature.value && (
                            <span className="text-primary font-semibold ml-1">
                              {feature.value}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-gray-300 text-sm italic">
                    <p>Contact us for detailed feature information</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

